import type {
    CancellationAwaitingReason,
    CancellationEstimate,
    CancellationProgress,
    CancellationReason,
    CancellationRequestingParty,
    CompletableReason,
    CompletionOption,
    RefundOwedStatus,
    ServiceHistorySource,
} from '../types/cancellation.types';
import { parseTenThousandths } from '@/shared/utils/moneyArithmetic';

export const REASON_LABEL: Record<CancellationReason, string> = {
    sold_vehicle: 'Sold the vehicle',
    changed_vehicle: 'Changed vehicle',
    moved_away: 'Moved away',
    bereavement: 'Bereavement',
    financial_hardship: 'Financial hardship',
    unhappy_with_service: 'Unhappy with the service',
    other: 'Other',
};

export const REQUESTING_PARTY_LABEL: Record<CancellationRequestingParty, string> = {
    customer: 'The customer',
    dealer: 'The dealer',
    motorplan: 'MotorPlan',
};

export const SERVICE_HISTORY_SOURCE_LABEL: Record<ServiceHistorySource, string> = {
    dealer_contact: 'Confirmed by a dealer contact',
    thirty_day_rule: '30-day rule',
    case_reference: 'Case reference',
};

export const SERVICE_HISTORY_DETAIL_LABEL: Record<ServiceHistorySource, string> = {
    dealer_contact: 'Name of the dealer contact',
    thirty_day_rule: 'Detail (optional)',
    case_reference: 'Case reference',
};

export const SCRIPT_POINTS: readonly string[] = [
    'The cancellation takes about 28 days to complete.',
    'Direct Debit collections stop once the cancellation is completed.',
    'Any refund is worked out after the cancellation fee is taken off.',
    'Any refund is paid to the customer.',
];

export const AWAITING_REASON_TEXT: Record<CancellationAwaitingReason, string> = {
    negative_balance: 'The customer has used more servicing than they have paid for.',
    elemis_not_eligible: 'MotorPlan Payments will not cancel this agreement.',
    elemis_already_cancelled: 'MotorPlan Payments shows this agreement as already cancelled on different terms.',
    elemis_cancelled_elsewhere: 'MotorPlan Payments shows this agreement was cancelled outside Artemis.',
    elemis_agreement_unlinked: 'This agreement is not linked to a MotorPlan Payments agreement.',
    refund_failed: 'The refund payment failed. The customer needs to contact MotorPlan.',
    integration_fault: 'MotorPlan Payments returned an error that needs a person to check.',
};

export interface StatusLineCopy {
    title: string;
    detail: string;
    tone: 'info' | 'warning';
}

export function statusLineCopy(progress: CancellationProgress): StatusLineCopy {
    switch (progress.kind) {
        case 'awaiting_clearance':
            return {
                title: 'Awaiting final payment to clear',
                detail: 'A payment is still in progress. The cancellation completes by itself once it has cleared.',
                tone: 'info',
            };
        case 'settling':
            return {
                title: 'Settling',
                detail: progress.refundPending
                    ? 'Waiting for the refund payment to be confirmed'
                    : 'The cancellation is being completed with MotorPlan Payments.',
                tone: 'info',
            };
        case 'awaiting_motorplan':
            return {
                title: 'Awaiting MotorPlan',
                detail: AWAITING_REASON_TEXT[progress.reason],
                tone: 'warning',
            };
    }
}

export const COMPLETION_OPTION_FOR_REASON: Record<CompletableReason, CompletionOption> = {
    negative_balance: 'cancel_with_negative_balance',
    elemis_not_eligible: 'close_in_artemis',
    elemis_already_cancelled: 'close_in_artemis',
    integration_fault: 'resend',
    refund_failed: 'retry_refund',
};

export interface CompletionCopy {
    title: string;
    effect: string;
    submitLabel: string;
    pendingLabel: string;
}

export const COMPLETION_COPY: Record<CompletionOption, CompletionCopy> = {
    cancel_with_negative_balance: {
        title: 'Cancel with no refund',
        effect: 'The agreement is cancelled and no refund is paid. This step does not record who covers the shortfall.',
        submitLabel: 'Cancel with no refund',
        pendingLabel: 'Cancelling...',
    },
    close_in_artemis: {
        title: 'Close in Artemis',
        effect: 'MotorPlan Payments is not contacted. The agreement is marked Cancelled here, and any refund owed is recorded.',
        submitLabel: 'Close in Artemis',
        pendingLabel: 'Closing...',
    },
    resend: {
        title: 'Send the cancellation again',
        effect: 'The same cancellation request is sent to MotorPlan Payments again, unchanged.',
        submitLabel: 'Send again',
        pendingLabel: 'Sending...',
    },
    retry_refund: {
        title: 'Raise the refund again',
        effect: 'The refund is raised again with MotorPlan Payments. Check that the customer has been in touch first.',
        submitLabel: 'Raise refund again',
        pendingLabel: 'Raising...',
    },
};

export const isNegativeAmount = (amount: string): boolean => amount.trim().startsWith('-');

export const isPositiveAmount = (amount: string | null): boolean => (amount === null ? false : (parseTenThousandths(amount.trim()) ?? 0n) > 0n);

export function isCompletableReason(reason: CancellationAwaitingReason): reason is CompletableReason {
    return reason in COMPLETION_OPTION_FOR_REASON;
}

export function feeRowLabel(estimate: CancellationEstimate, coolingOffEnds: string | null): string {
    if (!estimate.feeWaived) return 'Cancellation fee';
    return coolingOffEnds ? `Cancellation fee (waived, cooling-off period ends ${coolingOffEnds})` : 'Cancellation fee (waived)';
}

export const REFUND_STATUS_LABEL: Record<RefundOwedStatus, string> = {
    owed: 'Refund owed',
    in_payment: 'Refund being paid',
    paid: 'Refund paid',
    failed: 'Refund payment failed',
};
